import Icon from '../../components/Icon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AdminContentReportListItemDto,
  AdminStickerDto,
  AdminVenueBreadcrumbDto,
  ContentReportTargetType,
  STICKER_SETTINGS_DESCRIPTIONS,
  STICKER_SETTINGS_LABELS,
  STICKER_SETTINGS_RANGES,
  STICKER_SETTINGS_TOGGLE_KEYS,
  StickerSettingsConfig,
  StickerSettingsNumberKey,
  StickerSettingsToggleKey,
  validateStickerSettingsConfigForSave,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import StickerModal from '../../components/modals/StickerModal'
import VenueBreadcrumbModal from '../../components/modals/VenueBreadcrumbModal'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'

type Tab = 'moments' | 'notes' | 'reported' | 'settings'

const TABS: { key: Tab; label: string }[] = [
  { key: 'moments', label: 'Moments' },
  { key: 'notes', label: 'Notes' },
  { key: 'reported', label: 'Reported' },
  { key: 'settings', label: 'Settings' },
]

const EMPTY_PAGINATION = { total: 0, page: 1, pageSize: 10, totalPages: 0, hasNext: false, hasPrev: false }

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 260
  const paginationHeight = 60
  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

/**
 * Stickers (app "Stickers" screen content): Moments (photo stickers), Notes (venue breadcrumbs) and
 * users' reports on either. Admins pin moments ("Pinned by the Venue"), delete any note or moment,
 * and review reports: Keep = dismiss the reports, Remove = delete the item.
 */
const StickersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchValue } = useSearch()

  const tab = (searchParams.get('tab') as Tab) || 'moments'
  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || 'createdAt'
  const currentSortOrder = searchParams.get('sortOrder') || 'desc'

  const [moments, setMoments] = useState<AdminStickerDto[]>([])
  const [notes, setNotes] = useState<AdminVenueBreadcrumbDto[]>([])
  const [reports, setReports] = useState<AdminContentReportListItemDto[]>([])
  /** Open reports waiting for review, shown on the Reported tab from any tab. */
  const [openReportCount, setOpenReportCount] = useState<number | null>(null)
  const [pagination, setPagination] = useState(EMPTY_PAGINATION)
  const [selected, setSelected] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [openMoment, setOpenMoment] = useState<string | null>(null)
  const [openNote, setOpenNote] = useState<string | null>(null)
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  const fetchData = useCallback(async () => {
    setError(null)
    try {
      if (tab === 'moments') {
        const response = await adminService.getStickers(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
        )
        setMoments(response.stickers)
        setPagination(response.pagination)
      } else if (tab === 'notes') {
        const response = await adminService.getVenueBreadcrumbs(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
        )
        setNotes(response.breadcrumbs)
        setPagination(response.pagination)
      } else if (tab === 'settings') {
        setPagination(EMPTY_PAGINATION)
      } else {
        const response = await adminService.getContentReports(currentPage, currentPageSize)
        setReports(response.reports)
        setPagination(response.pagination)
        setOpenReportCount(response.pagination.total)
      }
    } catch (err) {
      console.log('Failed to fetch stickers', err)
      setError('Could not load. Try again.')
    }
  }, [tab, currentPage, currentPageSize, debouncedSearchValue, currentSortBy, currentSortOrder])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refreshOpenReportCount = useCallback(async () => {
    try {
      const response = await adminService.getContentReports(1, 1)
      setOpenReportCount(response.pagination.total)
    } catch {
      // The tab just shows no count.
    }
  }, [])

  useEffect(() => {
    refreshOpenReportCount()
  }, [refreshOpenReportCount, tab])

  // A selection only means something on the page it was made on.
  useEffect(() => {
    setSelected([])
  }, [tab, currentPage, currentPageSize, debouncedSearchValue])

  const setParam = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(changes).forEach(([key, value]) => (value === null ? next.delete(key) : next.set(key, value)))
    setSearchParams(next)
  }

  const handlePageChange = (page: number) => setParam({ page: page.toString() })

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const runBulk = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      setSelected([])
      await fetchData()
    } catch (err) {
      console.log('Bulk action failed', err)
      setError('Some items could not be updated. The list shows what changed.')
      await fetchData()
    } finally {
      setBusy(false)
    }
  }

  const pinSelected = (pinned: boolean) => runBulk(() => adminService.setStickersPinned(selected, pinned))

  const deleteSelected = () =>
    runBulk(async () => {
      const results = await Promise.allSettled(
        selected.map(id => (tab === 'moments' ? adminService.deleteSticker(id) : adminService.deleteVenueBreadcrumb(id))),
      )
      if (results.some(r => r.status === 'rejected')) throw new Error('partial')
    })

  /** Reported tab: Keep = unflag (dismisses the open reports), Remove = delete the item. */
  const resolveReport = (report: AdminContentReportListItemDto, remove: boolean) =>
    runBulk(async () => {
      const isNote = report.targetType === ContentReportTargetType.Note
      if (remove) {
        await (isNote ? adminService.deleteVenueBreadcrumb(report.targetPublicId) : adminService.deleteSticker(report.targetPublicId))
      } else {
        await (isNote
          ? adminService.unflagVenueBreadcrumb(report.targetPublicId)
          : adminService.unflagSticker(report.targetPublicId))
      }
    })

  const momentColumns = useMemo(
    () => [
      { label: '', accessor: 'imageUrl', type: 'thumbnail' },
      { label: 'From', accessor: 'author.firstName', type: 'fullName' },
      { label: 'Venue', accessor: 'venue.name', type: 'text' },
      { label: 'Caption', accessor: 'caption', type: 'text' },
      { label: 'Pinned', accessor: 'isPinned', type: 'flagged', sortable: false },
      { label: 'Likes', accessor: 'likeCount', type: 'number', sortable: false },
      { label: 'Reports', accessor: 'openReportCount', type: 'number', sortable: false },
      { label: 'Posted', accessor: 'createdAt', type: 'datetime' },
      { label: 'Expires', accessor: 'expiresAt', type: 'datetime' },
      { label: 'Flagged', accessor: 'isFlagged', type: 'flagged' },
    ],
    [],
  )

  const noteColumns = useMemo(
    () => [
      { label: 'From', accessor: 'author.firstName', type: 'fullName' },
      { label: 'Venue', accessor: 'venue.name', type: 'text' },
      { label: 'Note', accessor: 'content', type: 'text' },
      { label: 'Reactions', accessor: 'reactionCount', type: 'number', sortable: false },
      { label: 'Reports', accessor: 'openReportCount', type: 'number', sortable: false },
      { label: 'Posted', accessor: 'createdAt', type: 'datetime' },
      { label: 'Expires', accessor: 'expiresAt', type: 'datetime', sortable: false },
      { label: 'Flagged', accessor: 'isFlagged', type: 'flagged' },
    ],
    [],
  )

  const selectedLabel = `${selected.length} selected`
  const itemWord = tab === 'moments' ? 'moment' : 'note'

  return (
    <div className="users-container stickers-admin">
      <div className="users-header">
        <h2>Stickers</h2>
        {pagination.totalPages > 1 && (
          <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
        )}
      </div>

      <div className="stickers-admin-toolbar">
        <div className="stickers-admin-tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              className={`stickers-admin-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setParam({ tab: t.key, page: '1', sortBy: null, sortOrder: null })}
            >
              {t.label}
              {t.key === 'reported' && openReportCount ? (
                <span className="stickers-admin-tab-count">{openReportCount}</span>
              ) : null}
            </button>
          ))}
        </div>

        {(tab === 'moments' || tab === 'notes') && selected.length > 0 && (
          <div className="stickers-admin-bulk">
            <span>{selectedLabel}</span>
            {tab === 'moments' && (
              <>
                <button className="styled-button" disabled={busy} onClick={() => pinSelected(true)}>
                  Pin
                </button>
                <button className="styled-button" disabled={busy} onClick={() => pinSelected(false)}>
                  Unpin
                </button>
              </>
            )}
            <button className="styled-button danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          </div>
        )}
      </div>

      {error && <div className="page-error">{error}</div>}

      <div className="users-table-container">
        <div className="table-wrapper">
          {tab === 'moments' && (
            <Table
              id="stickers-moments"
              name="Moments"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={momentColumns}
              sourceData={moments}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AdminStickerDto) => setOpenMoment(row.publicId)}
              onSortChange={handleSortChange}
              disableGlobalSearch={true}
              disableClientSort={true}
              selectedKeys={selected}
              onSelectionChange={setSelected}
            />
          )}

          {tab === 'notes' && (
            <Table
              id="stickers-notes"
              name="Notes"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={noteColumns}
              sourceData={notes}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AdminVenueBreadcrumbDto) => setOpenNote(row.publicId)}
              onSortChange={handleSortChange}
              disableGlobalSearch={true}
              disableClientSort={true}
              selectedKeys={selected}
              onSelectionChange={setSelected}
            />
          )}

          {tab === 'settings' && <StickerSettingsPanel />}

          {tab === 'reported' && (
            <div className="stickers-admin-reports">
              {reports.length === 0 && <div className="no-data">No reports waiting for review.</div>}
              {reports.map(report => {
                const isNote = report.targetType === ContentReportTargetType.Note
                const reporter = `${report.reporter.firstName} ${report.reporter.lastName ?? ''}`.trim()
                const reportedAt = new Date(report.createdAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
                return (
                  <div key={report.publicId} className={`stickers-admin-report${report.target.isDeleted ? ' resolved' : ''}`}>
                    {report.target.imageUrl ? (
                      <div className="thumbnail" style={{ backgroundImage: `url(${report.target.imageUrl})` }} />
                    ) : (
                      <div
                        className="stickers-admin-report-kind"
                        title={isNote ? 'Note' : report.target.isDeleted ? 'Photo removed' : 'Moment'}
                      >
                        <Icon name={isNote ? 'NoteSticky' : 'Image'} />
                        {!isNote && report.target.isDeleted && <span>Removed</span>}
                      </div>
                    )}
                    <div className="stickers-admin-report-body">
                      <div className="stickers-admin-report-text">
                        <span className="stickers-admin-report-type">{isNote ? 'Note' : 'Moment'}</span>
                        {report.target.text || <span className="muted">Photo, no caption</span>}
                      </div>
                      <div className="stickers-admin-report-meta">
                        <span>
                          <Icon name="User" /> {report.target.authorName}
                        </span>
                        <span>
                          <Icon name="LocationDot" /> {report.target.venueName}
                        </span>
                      </div>
                      <div className="stickers-admin-report-meta">
                        {report.reason && (
                          <span className="stickers-admin-report-reason">
                            <Icon name="Flag" /> {report.reason}
                          </span>
                        )}
                        <span>
                          Reported by {reporter} · {reportedAt}
                        </span>
                      </div>
                    </div>
                    <div className="stickers-admin-report-actions">
                      {report.target.isDeleted ? (
                        <span className="stickers-admin-report-status">Already deleted</span>
                      ) : (
                        <>
                          <button
                            className="report-action"
                            disabled={busy}
                            title={isNote ? 'Open the note' : 'Open the moment'}
                            onClick={() => (isNote ? setOpenNote(report.targetPublicId) : setOpenMoment(report.targetPublicId))}
                          >
                            <Icon name="Eye" /> View
                          </button>
                          <button
                            className="report-action keep"
                            disabled={busy}
                            title="Keep it up and close the report"
                            onClick={() => resolveReport(report, false)}
                          >
                            <Icon name="Check" /> Keep
                          </button>
                          <button
                            className="report-action remove"
                            disabled={busy}
                            title="Delete it for everyone"
                            onClick={() => resolveReport(report, true)}
                          >
                            <Icon name="TrashCan" /> Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${selected.length} ${itemWord}${selected.length === 1 ? '' : 's'}?`}
        confirmLabel={busy ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        isLoading={busy}
        onConfirm={async () => {
          await deleteSelected()
          setConfirmDelete(false)
        }}
      >
        <div>They will be hidden from users. Content is retained for audit.</div>
      </ConfirmationModal>

      <StickerModal
        isOpen={!!openMoment}
        onClose={() => setOpenMoment(null)}
        publicId={openMoment}
        onModerationChange={() => fetchData()}
        onDelete={async publicId => {
          await adminService.deleteSticker(publicId)
        }}
        onDeleted={() => {
          setOpenMoment(null)
          fetchData()
        }}
      />

      <VenueBreadcrumbModal
        isOpen={!!openNote}
        onClose={() => setOpenNote(null)}
        publicId={openNote}
        onModerationChange={() => fetchData()}
        onDelete={async publicId => {
          await adminService.deleteVenueBreadcrumb(publicId)
        }}
        onDeleted={() => {
          setOpenNote(null)
          fetchData()
        }}
      />
    </div>
  )
}

export default StickersPage

/** Number fields are edited as text; on/off settings stay booleans. */
type StickerSettingsDraft = Record<StickerSettingsNumberKey, string> & Record<StickerSettingsToggleKey, boolean>

const STICKER_SETTINGS_NUMBER_KEYS = Object.keys(STICKER_SETTINGS_RANGES) as StickerSettingsNumberKey[]

/**
 * Settings tab: limits, lifetimes, and whether new notes and moments start as permanent in the
 * app. Saved values apply to new posts right away.
 */
const StickerSettingsPanel = () => {
  const [saved, setSaved] = useState<StickerSettingsConfig | null>(null)
  const [draft, setDraft] = useState<StickerSettingsDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const toDraft = (config: StickerSettingsConfig): StickerSettingsDraft => ({
    ...config,
    ...(Object.fromEntries(STICKER_SETTINGS_NUMBER_KEYS.map(k => [k, String(config[k])])) as Record<StickerSettingsNumberKey, string>),
  })

  useEffect(() => {
    adminService
      .getStickerSettings()
      .then(config => {
        setSaved(config)
        setDraft(toDraft(config))
      })
      .catch(() => setMessage('Could not load settings.'))
  }, [])

  if (!draft || !saved) {
    return <div className="no-data">{message ?? 'Loading settings…'}</div>
  }

  const parsed: StickerSettingsConfig = {
    ...draft,
    ...(Object.fromEntries(
      STICKER_SETTINGS_NUMBER_KEYS.map(k => [k, draft[k].trim() === '' ? NaN : Number(draft[k])]),
    ) as Record<StickerSettingsNumberKey, number>),
  }
  const validation = validateStickerSettingsConfigForSave(parsed)
  const dirty = (Object.keys(saved) as (keyof StickerSettingsConfig)[]).some(k => parsed[k] !== saved[k])

  const save = async () => {
    if (validation) return
    setSaving(true)
    setMessage(null)
    try {
      const next = await adminService.setStickerSettings(parsed)
      setSaved(next)
      setDraft(toDraft(next))
      setMessage('Saved. New notes and moments use these settings.')
    } catch (err: any) {
      setMessage(err?.response?.data?.friendlyMessage ?? 'Could not save settings.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stickers-admin-settings">
      {STICKER_SETTINGS_TOGGLE_KEYS.map(key => (
        <label key={key} className="stickers-admin-setting">
          <span className="stickers-admin-setting-label">{STICKER_SETTINGS_LABELS[key]}</span>
          <input
            type="checkbox"
            checked={draft[key]}
            onChange={ev => setDraft(current => (current ? { ...current, [key]: ev.target.checked } : current))}
          />
          <span className="stickers-admin-setting-hint">{STICKER_SETTINGS_DESCRIPTIONS[key]}</span>
        </label>
      ))}
      {STICKER_SETTINGS_NUMBER_KEYS.map(key => (
        <label key={key} className="stickers-admin-setting">
          <span className="stickers-admin-setting-label">{STICKER_SETTINGS_LABELS[key]}</span>
          <input
            type="number"
            min={STICKER_SETTINGS_RANGES[key].min}
            max={STICKER_SETTINGS_RANGES[key].max}
            step={1}
            value={draft[key]}
            onChange={ev => setDraft(current => (current ? { ...current, [key]: ev.target.value } : current))}
          />
          <span className="stickers-admin-setting-hint">
            {STICKER_SETTINGS_DESCRIPTIONS[key]} ({STICKER_SETTINGS_RANGES[key].min}–{STICKER_SETTINGS_RANGES[key].max})
          </span>
        </label>
      ))}
      {validation && <div className="page-error">{validation.message}</div>}
      {message && <div className="stickers-admin-setting-message">{message}</div>}
      <div className="stickers-admin-setting-actions">
        <button className="styled-button" disabled={!dirty || saving} onClick={() => setDraft(toDraft(saved))}>
          Reset
        </button>
        <button className="styled-button" disabled={!dirty || !!validation || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
